import {
  CommentContent,
  CommentsIllustration,
  ConversationMessageEventEnum,
  ConversationMessageTypeEnum,
  workV4Query,
  IChatNotificationResult,
  IConversation,
  IConversationMessage,
  IConversationMessageMentions,
  IConversationMessageMentionsUser,
  IWorkUsers,
  Menu,
  queryClient,
  squashWorkUsers,
  useCurrentUserInfo,
  usePrevious,
  UserAvatarTooltip,
  useSignalRV4EventListener,
} from '@diana/core';
import { MoreHoriz } from '@mui/icons-material';
import {
  Box,
  IconButton,
  ListItem,
  Popover,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { DOMNode } from 'html-react-parser';
import parse from 'html-react-parser';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import { WORK_PAGE_NS } from '../i18n';
import { CommentsListBox, EmptyBox } from './Comments.styles';
import { CommentsEditCommentBox } from './CommentsEditComment';
import { CommentsNewCommentBox } from './CommentsNewComment';
import { CommentsUserInfo } from './CommentsUserInfo';
import { dataMentionAttribute, dataMentionUserIdAttribute } from './Support';

export interface CommentsProps {
  workId: string;
  users: IWorkUsers;
}

export const Comments = (props: CommentsProps) => {
  const { workId, users } = props;

  const { palette } = useTheme();
  const { t } = useTranslation(WORK_PAGE_NS);
  const info = useCurrentUserInfo();
  const bottomDivOfListRef = useRef<HTMLDivElement>(null);
  const [targetComment, setTargetComment] = useState<IConversationMessage | null>(null);
  const [idsToHighlight, setIdsToHighLight] = useState<string[]>([]);
  const [isUpdateEditorOpen, setIsUpdateEditorOpen] = useState<boolean>(false);
  const [, setForceUpdate] = useState(1);
  const [messageItemAnchorElement, setMessageItemAnchorElement] =
    useState<HTMLButtonElement | null>(null);

  const workUsers = squashWorkUsers(users);

  const [mentionedUserPreview, setMentionedUserPreview] = useState<{
    element: HTMLSpanElement | null;
    user: IConversationMessageMentionsUser | null;
  }>({ element: null, user: null });

  const onLeave = useDebouncedCallback(() => {
    setMentionedUserPreview((current) => ({ ...current, element: null }));
  }, 250);

  const onEnter = useDebouncedCallback(
    (args: { element: HTMLSpanElement; user: IConversationMessageMentionsUser }) => {
      setMentionedUserPreview(args);
    },
    350,
  );

  const scrollToBottom = useCallback(() => {
    bottomDivOfListRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const forceUpdate = useDebouncedCallback(() => {
    setForceUpdate((current) => current + 1);
  }, 50);

  const getConversationQuery = workV4Query.getWorkConversation({ workId });
  const { data: conversation, isLoading: isLoadingConversation } = useQuery({
    queryKey: getConversationQuery.key,
    queryFn: getConversationQuery.fn,
  });

  const { mutateAsync: deleteMessageFn, isPending: isLoadingDeleteMessage } = useMutation({
    mutationFn: (fnArgs: { messageId: string }) => {
      const { messageId } = fnArgs;
      return workV4Query.deleteWorkConversationMessage({ workId, messageId }).fn();
    },
  });

  const updateConversationMessage = useDebouncedCallback((payload: IChatNotificationResult) => {
    // handle new message highlight
    const payloadMessage = payload.message;
    setIdsToHighLight([payloadMessage.id]);
    setTimeout(() => setIdsToHighLight([]), 3000);

    queryClient.setQueryData<IConversation | undefined>(getConversationQuery.key, (current) => {
      if (!current) return undefined;

      const { messages = [] } = current;

      switch (payloadMessage.messageEvent) {
        case ConversationMessageEventEnum.ADDED: {
          messages.push(payloadMessage);
          break;
        }
        case ConversationMessageEventEnum.UPDATED: {
          const foundMessageIndex = messages.findIndex(
            (message) => message.id === payloadMessage.id,
          );

          if (foundMessageIndex >= 0) messages.splice(foundMessageIndex, 1, payloadMessage);
          break;
        }
        case ConversationMessageEventEnum.DELETED: {
          const foundMessageIndex = messages.findIndex(
            (message) => message.id === payloadMessage.id,
          );

          if (foundMessageIndex >= 0) messages.splice(foundMessageIndex, 1);
          break;
        }
        default: {
          return {
            ...current,
            messages,
          } as IConversation;
        }
      }
    });
    // for some reason setQueryData isn't rerendering the page.
    forceUpdate();
  }, 200);

  useSignalRV4EventListener(
    'Message',
    useCallback(
      (payload) => {
        if (!conversation) return;
        if (payload.message.channelId !== conversation.channelId) return;

        updateConversationMessage(payload);
      },
      [conversation, updateConversationMessage],
    ),
  );

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, conversationMessage: IConversationMessage) => {
      event.stopPropagation();
      setIsUpdateEditorOpen(false);
      setMessageItemAnchorElement(event.currentTarget);
      setTargetComment(conversationMessage);
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setMessageItemAnchorElement(null);
  }, []);

  const onEditComment = useCallback(() => {
    if (!targetComment) return;

    setIsUpdateEditorOpen(true);
    handleMenuClose();
  }, [targetComment, handleMenuClose]);

  const onDeleteComment = useCallback(async () => {
    if (!targetComment) return;

    try {
      await deleteMessageFn({ messageId: targetComment.id });
    } catch (e) {}

    handleMenuClose();
  }, [targetComment, handleMenuClose, deleteMessageFn]);

  const parseHTMLComment = useCallback(
    (args: { domNode: DOMNode; mentions: IConversationMessageMentions }) => {
      const { domNode, mentions } = args;

      if ('name' in domNode && 'attribs' in domNode && domNode.name === 'span') {
        const { attribs } = domNode;

        const dataMention = attribs[dataMentionAttribute];
        const dataMentionUserId = attribs[dataMentionUserIdAttribute];

        if (attribs.class === 'mention' && dataMention && dataMentionUserId) {
          const additionalProps = {
            [dataMentionAttribute]: dataMention,
            [dataMentionUserIdAttribute]: dataMentionUserId,
          };

          const clickedUser = mentions.users?.find((e) => e.userId === dataMentionUserId);
          if (!clickedUser) return;

          return (
            <span
              className="mention"
              {...additionalProps}
              style={{ cursor: 'default' }}
              onMouseEnter={(e) => onEnter({ element: e.currentTarget, user: clickedUser })}
              onMouseLeave={() => {
                onEnter.cancel();
                onLeave();
              }}
            >
              {dataMention}
            </span>
          );
        }
      }
    },
    [onEnter, onLeave],
  );

  const renderSkeletons = useMemo(() => {
    return [...Array(10)].map((_, index) => (
      <Stack
        key={index}
        direction="row"
        spacing={2}
        sx={{
          px: 2,
          mt: 2,
          mb: 2,
        }}
      >
        <Skeleton variant="circular" width="30px" height="30px" />
        <Skeleton width="80%" />
      </Stack>
    ));
  }, []);

  const messages = conversation?.messages?.filter(
    (e) => e.messageType === ConversationMessageTypeEnum.USER,
  );

  const previousMessagesLength = usePrevious(messages?.length);
  useEffect(() => {
    if (isUpdateEditorOpen) {
      return;
    }

    if (previousMessagesLength !== messages?.length) {
      scrollToBottom();
    }
  }, [conversation, messages, previousMessagesLength, scrollToBottom, isUpdateEditorOpen]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <CommentsListBox
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {isLoadingConversation && renderSkeletons}
        {messages?.length === 0 && (
          <EmptyBox>
            <CommentsIllustration />
            <Typography mt={1} sx={{ color: palette.text.secondary }}>
              {t('commentsDrawer.noComments')}
            </Typography>
          </EmptyBox>
        )}
        {conversation && (
          <Stack spacing={2}>
            {messages?.map((message) => (
              <ListItem
                key={message.id}
                sx={{
                  backgroundColor:
                    (idsToHighlight.length && idsToHighlight.some((i) => i === message.id)) ||
                    targetComment?.id === message.id
                      ? palette.mode === 'dark'
                        ? palette.background.card
                        : palette.common.white
                      : null,
                  pt: '8px!important',
                  pb:
                    targetComment?.id === message.id && !Boolean(messageItemAnchorElement)
                      ? null
                      : '8px!important',
                  mt: '0!important',
                  borderBottom: `1px solid ${palette.divider}`,
                }}
              >
                <Box sx={{ paddingRight: 2, paddingLeft: 2 }}>
                  <CommentsUserInfo message={message} />

                  {info?.user.id === message.fromUserId && (
                    <IconButton
                      aria-label="options"
                      color="secondary"
                      onClick={(e) => handleMenuClick(e, message)}
                      sx={{
                        padding: 0.75,
                        marginLeft: 'auto',
                      }}
                    >
                      <MoreHoriz />
                    </IconButton>
                  )}

                  <Menu
                    sx={{ '.MuiMenu-paper': { boxShadow: 'none!important' } }}
                    open={Boolean(messageItemAnchorElement)}
                    anchorEl={messageItemAnchorElement}
                    onClose={handleMenuClose}
                    actions={[
                      {
                        title: t('commentsDrawer.edit'),
                        onClick: onEditComment,
                      },
                      {
                        title: isLoadingDeleteMessage
                          ? t('commentsDrawer.deleting')
                          : t('commentsDrawer.delete'),
                        onClick: onDeleteComment,
                        color: 'error.main',
                        disabled: info?.user.id !== message.fromUserId,
                      },
                    ]}
                    position="bottomRightInner"
                  />
                </Box>

                {targetComment && isUpdateEditorOpen && targetComment.id === message.id ? (
                  <CommentsEditCommentBox
                    users={workUsers}
                    workId={workId}
                    message={message}
                    onCancel={() => {
                      setTargetComment(null);
                      setIsUpdateEditorOpen(false);
                    }}
                  />
                ) : (
                  <Box sx={{ paddingRight: 2, paddingLeft: 2, paddingBottom: 0 }}>
                    <CommentContent className="ck ck-content">
                      {parse(message.messageText, {
                        replace: (domNode) =>
                          parseHTMLComment({ domNode, mentions: message.mentions }),
                      })}
                    </CommentContent>
                  </Box>
                )}
              </ListItem>
            ))}
            <div ref={bottomDivOfListRef} />
          </Stack>
        )}
      </CommentsListBox>
      {!isUpdateEditorOpen && <CommentsNewCommentBox users={workUsers} workId={workId} />}
      <Popover
        disableAutoFocus
        open={Boolean(mentionedUserPreview.element)}
        anchorEl={mentionedUserPreview.element}
        sx={{ pointerEvents: 'none', mb: 2 }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box onMouseEnter={onLeave.cancel} onMouseLeave={onLeave} sx={{ pointerEvents: 'all' }}>
          <UserAvatarTooltip
            user={{
              ...mentionedUserPreview.user,
              name: mentionedUserPreview.user?.displayName,
            }}
          />
        </Box>
      </Popover>
    </Box>
  );
};
